/**
 * Referral code utility functions.
 * Referral attribution is driven by referral cookie (getReferralCookie) and manual user input only.
 * Legacy URL (?ref=) and sessionStorage sources have been removed.
 */

import { authenticatedFetch } from './auth'
import { API_BASE_URL, API_ENDPOINTS } from './config'

const REFERRAL_LINK_KEY = 'referral_link'
const REFERRAL_CODE_KEY = 'referral_code'

/**
 * Submit referral code to API after login
 * @param referralCode - The referral code to submit
 * @param apiToken - The user's authentication token
 * @returns Response with success status and message
 */
export async function submitReferralCode(
  referralCode: string,
  apiToken: string
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.SUBMIT_REFERRAL_CODE}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        referral_code: referralCode,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.detail || errorData.message || 'Failed to submit referral code'
      )
    }

    const data: { success: boolean; message: string } = await response.json()
    return data
  } catch (error) {
    console.error('Error submitting referral code:', error)
    throw error
  }
}

/**
 * Referral link generation and caching functions
 */

export type ReferralLinkResponse = {
  referralLink: string
  referralCode: string
}

export type ReferralData = {
  referralLink: string
  referralCode: string
}

/**
 * Format the referral share message with link and code
 */
export function formatReferralShareMessage(
  referralLink: string,
  referralCode: string
): string {
  return `🚀 Join me on meritlens, a powerful platform for interview preparation and skill development!

I've been using meritlens to practice mock interviews and sharpen my skills, and it's been really helpful. You should definitely give it a try!

👉 Sign up using my referral link:
${referralLink}

OR
👉 Enter the referral code during registration: ${referralCode}

Let's grow and level up together! 💪✨
`
}

/**
 * Generate or fetch referral link and code from API
 * @returns Referral data (link and code) or null if error
 */
export async function generateReferralLink(): Promise<ReferralData | null> {
  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.REFERRAL_LINK}`,
      {
        method: 'GET',
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.message || errorData.detail || 'Failed to generate referral link'
      )
    }

    const data: ReferralLinkResponse = await response.json()
    cacheReferralLink(data.referralLink)
    cacheReferralCode(data.referralCode)
    return { referralLink: data.referralLink, referralCode: data.referralCode }
  } catch (error) {
    console.error('Error generating referral link:', error)
    throw error
  }
}

/**
 * Get cached referral link from sessionStorage
 * @returns Cached referral link or null if not found
 */
export function getCachedReferralLink(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return sessionStorage.getItem(REFERRAL_LINK_KEY)
  } catch (error) {
    console.error('Error retrieving cached referral link:', error)
    return null
  }
}

/**
 * Get cached referral code from sessionStorage
 * @returns Cached referral code or null if not found
 */
export function getCachedReferralCode(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return sessionStorage.getItem(REFERRAL_CODE_KEY)
  } catch (error) {
    console.error('Error retrieving cached referral code:', error)
    return null
  }
}

/**
 * Get cached referral data (link and code)
 * @returns Cached referral data or null if either is missing
 */
export function getCachedReferralData(): ReferralData | null {
  const link = getCachedReferralLink()
  const code = getCachedReferralCode()
  if (link && code) {
    return { referralLink: link, referralCode: code }
  }
  return null
}

/**
 * Cache referral link in sessionStorage
 * @param link - Referral link to cache
 */
export function cacheReferralLink(link: string): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    if (link && link.trim().length > 0) {
      sessionStorage.setItem(REFERRAL_LINK_KEY, link.trim())
    }
  } catch (error) {
    console.error('Error caching referral link:', error)
    // Continue execution even if storage fails
  }
}

/**
 * Cache referral code in sessionStorage
 * @param code - Referral code to cache
 */
export function cacheReferralCode(code: string): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    if (code && code.trim().length > 0) {
      sessionStorage.setItem(REFERRAL_CODE_KEY, code.trim())
    }
  } catch (error) {
    console.error('Error caching referral code:', error)
    // Continue execution even if storage fails
  }
}

